import { Restoration, RestorationPrice } from 'models/order';

/**
 * DD-2506: one priced line of an order, read from its `restorations` rows. Replaces the
 * legacy `Order.priceSpecification` list: every action and material row of every restoration.
 */
export type RestorationPriceRow = {
    /** The row's own id, stable across order versions. */
    _id: string;
    kind: 'ACTION' | 'MATERIAL';
    /** Catalogue action id (actions only): what production value reports match on. */
    actionId?: string | null;
    code?: string | null;
    name: string;
    /** `${code} ${name}` for an action, the name for a material. */
    description: string;
    quotable: boolean;
    isTioTandvard: boolean;
    quantity: number;
    /** False when the row carries no unit price (e.g. a quotable action not yet priced). */
    hasPrice: boolean;
    unitPrice: number;
    /** The price-list unit price before any discount. */
    originalUnitPrice?: number | null;
    vatPercent: number;
    vat: number;
    totalWithoutVat: number;
    totalWithVat: number;
};

const priceFields = (price?: RestorationPrice | null) => {
    const totalWithoutVat = price?.totalWithoutVat ?? 0;
    const totalWithVat = price?.totalWithVat ?? 0;
    return {
        quantity: price?.quantity ?? 0,
        hasPrice: price?.unitPrice != null,
        unitPrice: price?.unitPrice ?? 0,
        originalUnitPrice: price?.originalUnitPrice,
        vatPercent: price?.vatPercent ?? 0,
        vat: totalWithVat - totalWithoutVat,
        totalWithoutVat,
        totalWithVat,
    };
};

/** Every action row, then every material row, of each restoration in order. */
export const restorationPriceRows = (restorations?: Restoration[] | null): RestorationPriceRow[] =>
    (restorations ?? []).flatMap((restoration) => [
        ...(restoration.actions ?? []).map(
            (row): RestorationPriceRow => ({
                _id: row._id,
                kind: 'ACTION',
                actionId: row.actionId,
                code: row.code,
                name: row.name,
                description: `${row.code || ''} ${row.name || ''}`.trim(),
                quotable: Boolean(row.quotable),
                isTioTandvard: Boolean(row.isTioTandvard),
                ...priceFields(row.price),
            }),
        ),
        ...(restoration.materials ?? []).map(
            (row): RestorationPriceRow => ({
                _id: row._id,
                kind: 'MATERIAL',
                code: row.code,
                name: row.name,
                description: row.name || '',
                quotable: false,
                isTioTandvard: Boolean(row.isTioTandvard),
                ...priceFields(row.price),
            }),
        ),
    ]);

export const restorationActionRows = (restorations?: Restoration[] | null): RestorationPriceRow[] =>
    restorationPriceRows(restorations).filter((row) => row.kind === 'ACTION');

export const hasRestorationAction = (restorations?: Restoration[] | null): boolean =>
    (restorations ?? []).some((restoration) => (restoration.actions ?? []).length > 0);

/** Action rows keyed by catalogue action id, as production value reports look them up. */
export const restorationActionRowsByActionId = (
    restorations?: Restoration[] | null,
): Map<string, RestorationPriceRow> =>
    new Map(
        restorationActionRows(restorations)
            .filter((row) => !!row.actionId)
            .map((row) => [String(row.actionId), row]),
    );
